/**
 * Sync Service
 *
 * Zentrale Synchronisierungslogik für alle Offline-Daten.
 * Wird aufgerufen wenn die App wieder online geht.
 */

import { zeiterfassungApi } from '../api/zeiterfassungApi';
import { unterschriftApi } from '../api/unterschriftApi';
import { fotoApi } from '../api/fotoApi';
import { syncAllPendingArbeitsnachweise, getPendingArbeitsnachweise } from '../api/arbeitsnachweisApi';
import { getPendingCount, getCacheStats } from '../utils/offlineDB';

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.listeners = [];
  }

  // Event Listener für Sync-Events
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  // Notify all listeners
  notify(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('SyncService listener error:', error);
      }
    });
  }

  /**
   * Synchronisiert alle ausstehenden Offline-Daten
   */
  async syncAll() {
    if (this.isSyncing) {
      console.log('⏳ Sync bereits in Bearbeitung...');
      return { skipped: true };
    }

    if (!navigator.onLine) {
      console.log('📴 Noch offline - Sync nicht möglich');
      return { offline: true };
    }

    this.isSyncing = true;
    this.notify('sync-start', {});

    console.log('🔄 Starte Synchronisierung...');

    const results = {
      zeiterfassung: { synced: 0, failed: 0 },
      unterschriften: { synced: 0, failed: 0 },
      fotos: { synced: 0, failed: 0 },
      arbeitsnachweise: { synced: 0, failed: 0 },
      totalSynced: 0,
      totalFailed: 0
    };

    try {
      // 1. Zeiterfassung synchronisieren
      console.log('⏱️ Synchronisiere Zeiterfassung...');
      results.zeiterfassung = await zeiterfassungApi.syncOfflineData();
      console.log(`   ✅ ${results.zeiterfassung.synced} synchronisiert, ❌ ${results.zeiterfassung.failed} fehlgeschlagen`);

      // 2. Unterschriften synchronisieren
      console.log('✍️ Synchronisiere Unterschriften...');
      results.unterschriften = await unterschriftApi.syncOfflineData();
      console.log(`   ✅ ${results.unterschriften.synced} synchronisiert, ❌ ${results.unterschriften.failed} fehlgeschlagen`);

      // 3. Fotos synchronisieren
      console.log('📸 Synchronisiere Fotos...');
      results.fotos = await fotoApi.syncOfflineData();
      console.log(`   ✅ ${results.fotos.synced} synchronisiert, ❌ ${results.fotos.failed} fehlgeschlagen`);

      // 4. Arbeitsnachweise synchronisieren
      console.log('📄 Synchronisiere Arbeitsnachweise...');
      results.arbeitsnachweise = await syncAllPendingArbeitsnachweise();
      console.log(`   ✅ ${results.arbeitsnachweise.synced} synchronisiert, ❌ ${results.arbeitsnachweise.failed} fehlgeschlagen`);

      // Gesamt berechnen
      results.totalSynced =
        results.zeiterfassung.synced +
        results.unterschriften.synced +
        results.fotos.synced +
        results.arbeitsnachweise.synced;

      results.totalFailed =
        results.zeiterfassung.failed +
        results.unterschriften.failed +
        results.fotos.failed +
        results.arbeitsnachweise.failed;

      console.log(`\n🎉 Synchronisierung abgeschlossen:`);
      console.log(`   Gesamt synchronisiert: ${results.totalSynced}`);
      console.log(`   Gesamt fehlgeschlagen: ${results.totalFailed}`);

      this.notify('sync-complete', results);

      return results;

    } catch (error) {
      console.error('❌ Synchronisierung fehlgeschlagen:', error);
      this.notify('sync-error', { error: error.message });
      return { error: error.message, ...results };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Gibt die Anzahl der ausstehenden Offline-Einträge zurück
   */
  async getPendingCount() {
    return await getPendingCount();
  }

  /**
   * Gibt detaillierte Pending-Counts für jede Kategorie zurück
   */
  async getDetailedPendingCount() {
    const zeit = await zeiterfassungApi.getPendingOfflineCount();
    const unterschriften = await unterschriftApi.getPendingOfflineCount();
    const fotos = await fotoApi.getPendingOfflineCount();
    const arbeitsnachweisePending = await getPendingArbeitsnachweise();
    const arbeitsnachweise = arbeitsnachweisePending.length;

    return {
      zeiterfassung: zeit,
      unterschriften: unterschriften,
      fotos: fotos,
      arbeitsnachweise: arbeitsnachweise,
      total: zeit + unterschriften + fotos + arbeitsnachweise
    };
  }

  /**
   * Prüft ob Sync notwendig ist
   */
  async needsSync() {
    const count = await this.getPendingCount();
    return count > 0;
  }

  /**
   * Gibt Cache-Statistiken zurück
   */
  async getCacheStats() {
    return await getCacheStats();
  }

  /**
   * Prüft ob gerade synchronisiert wird
   */
  isSyncInProgress() {
    return this.isSyncing;
  }
}

// Singleton instance
const syncService = new SyncService();

export default syncService;
